import { useRef, useState } from "react";
import { Button, SelectField, TextField } from "@/components/ui/FormControls";
import { api } from "@/lib/api";
import {
  downloadCsvTemplate,
  downloadExcelTemplate,
  IMPORT_HEADERS,
  IMPORT_TEMPLATE_VERSION,
  parseImportFile,
  type ImportRecord,
} from "./import-template";

export interface ImportResult {
  id: string;
  fileName: string;
  period: string;
  status: string;
  totalRows: number;
  validRows: number;
  warningRows: number;
  errorRows: number;
  createdAt: string;
}

interface ImportPanelProps {
  projectId?: string;
  scenarioId?: string | null;
  ensureScenarioId?: () => Promise<string>;
  title?: string;
  description?: string;
  badge?: string;
  connectionId?: string;
  connections?: Array<{ id: string; name: string; status: string; sourceType?: string }>;
  onConnectionChange?: (connectionId: string) => void;
  onImported: (result: ImportResult, records: ImportRecord[]) => void;
}

export function ImportPanel({ projectId, scenarioId, ensureScenarioId, title = "활동자료 가져오기", description = "플랫폼 표준 양식에 입력한 CSV 또는 XLSX 파일만 업로드합니다. 열 이름과 순서는 바꾸지 마세요.", badge = "표준 양식", connectionId, connections = [], onConnectionChange, onImported }: ImportPanelProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState("");
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<string[][]>([]);
  const [records, setRecords] = useState<ImportRecord[]>([]);
  const [period, setPeriod] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function readFile(file: File) {
    setError(null);
    try {
      const parsed = await parseImportFile(file);
      setFileName(file.name);
      setHeaders(parsed.headers);
      setRows(parsed.rows);
      setRecords(parsed.records);
    } catch (e) {
      setError(e instanceof Error ? e.message : "파일을 읽을 수 없습니다.");
      setFileName("");
      setHeaders([]);
      setRows([]);
      setRecords([]);
    }
  }

  async function submit() {
    setError(null);
    if (!fileName || !headers.length || !records.length) {
      setError("표준 양식 파일을 먼저 선택해 주세요.");
      return;
    }
    setBusy(true);
    try {
      const targetId = scenarioId ?? (ensureScenarioId ? await ensureScenarioId() : null);
      const endpoint = projectId ? `/workspace/projects/${projectId}/imports` : targetId ? `/scenarios/${targetId}/imports` : null;
      if (!endpoint) throw new Error("먼저 시나리오를 저장해 주세요.");
      const result = await api<{ import: ImportResult }>(endpoint, {
        method: "POST",
        json: {
          fileName,
          period: period || "기간 미지정",
          templateVersion: IMPORT_TEMPLATE_VERSION,
          headers,
          ...(projectId && connectionId ? { connectionId } : {}),
          rows: records.map((record) => ({ ...record, period: record.period || period })),
        },
      });
      onImported(result.import, records);
    } catch (e) {
      setError(e instanceof Error ? e.message : "데이터를 가져오지 못했습니다.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="workspace-card">
      <div className="workspace-card-head">
        <div>
          <h2 className="workspace-card-title">{title}</h2>
          <p className="workspace-card-desc">{description}</p>
        </div>
        <span className="badge-muted">{badge}</span>
      </div>

      <div className="workspace-template-actions">
        <Button variant="secondary" onClick={downloadExcelTemplate}>엑셀 양식 다운로드</Button>
        <Button variant="secondary" onClick={downloadCsvTemplate}>CSV 양식 다운로드</Button>
      </div>

      {projectId && connections.length > 0 && (
        <div className="workspace-import-connection">
          <SelectField label="연결할 데이터 소스" hint="연결을 선택하면 업로드 시 마지막 동기화 시각과 다음 예정 시각을 갱신합니다." value={connectionId ?? ""} onChange={(event) => onConnectionChange?.(event.target.value)}>
            <option value="">연결 없이 한 번 업로드</option>
            {connections.filter((connection) => connection.status === "active" && (!connection.sourceType || connection.sourceType === "standard_file")).map((connection) => <option key={connection.id} value={connection.id}>{connection.name}</option>)}
          </SelectField>
        </div>
      )}

      <input ref={fileRef} type="file" accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="sr-only" onChange={(event) => { const file = event.target.files?.[0]; event.target.value = ""; if (file) void readFile(file); }} />
      <button type="button" className="workspace-file-picker" onClick={() => fileRef.current?.click()}>
        <span className="workspace-file-picker-title">{fileName || "표준 CSV 또는 XLSX 파일을 선택하세요"}</span>
        <span className="workspace-file-picker-desc">활동자료 시트, 최대 5,000행</span>
      </button>

      {headers.length > 0 && (
        <>
          <div className="workspace-import-summary">
            <span>열 구성 확인 완료</span>
            <span>{rows.length}행</span>
          </div>
          <div className="workspace-preview">
            <div className="workspace-preview-head"><strong>미리보기</strong><span>처음 5행</span></div>
            <div className="table-scroll workspace-preview-scroll">
              <table className="admin-table">
                <thead><tr>{IMPORT_HEADERS.map((header) => <th key={header}>{header}</th>)}</tr></thead>
                <tbody>{rows.slice(0, 5).map((values, rowIndex) => <tr key={rowIndex}>{IMPORT_HEADERS.map((header, columnIndex) => <td key={`${header}-${columnIndex}`}>{values[columnIndex] || "없음"}</td>)}</tr>)}</tbody>
              </table>
            </div>
          </div>
          <div className="workspace-form-grid workspace-import-period">
            <TextField label="파일의 기본 기간" placeholder="예: 2026-09" value={period} onChange={(event) => setPeriod(event.target.value)} hint="기간 열이 비어 있는 행에 사용합니다." />
          </div>
          <div className="workspace-actions">
            <Button variant="primary" disabled={busy} onClick={() => void submit()}>{busy ? "검증 중..." : "데이터 검증하고 저장"}</Button>
          </div>
        </>
      )}
      {error && <p className="note note-danger text-caption" role="alert">{error}</p>}
    </section>
  );
}
